import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '@/utils/api';
import { Bell, X, Check, ChevronRight, MessageSquare, AlertCircle, Info, Gift, Image as ImageIcon } from 'lucide-react';
import { format } from 'date-fns';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';

const API_BASE = API.replace('/api', '');

// Notification Bell Icon with dropdown panel
export const NotificationBell = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user, loading: authLoading } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isB2BUser, setIsB2BUser] = useState(false);
  const [b2bClientId, setB2bClientId] = useState(null);
  const dropdownRef = useRef(null);

  // Get theme colors with fallbacks
  const themeColors = {
    background: theme?.background_secondary || theme?.background || '#1E293B',
    backgroundSecondary: theme?.background || '#0F172A',
    primary: theme?.primary || '#06B6D4',
    secondary: theme?.secondary || '#14B8A6',
    text: theme?.text || '#F8FAFC',
    textSecondary: theme?.text_secondary || '#94A3B8',
    border: theme?.border || theme?.primary || '#334155',
    buttonBg: theme?.mode === 'flat' 
      ? theme?.primary 
      : `linear-gradient(135deg, ${theme?.primary || '#06B6D4'}, ${theme?.secondary || '#14B8A6'})`,
  };

  const fetchNotifications = useCallback(async () => {
    // Wait for auth to be ready
    if (authLoading || !user) return;
    
    try {
      // Use user from AuthContext instead of making a separate /auth/me call
      // Track if user is B2B for navigation purposes
      setIsB2BUser(!!user?.client_id);
      if (user?.client_id) {
        setB2bClientId(user.client_id);
      }
      
      // B2B users get their client's notifications, regular users get admin notifications
      const endpoint = user?.client_id 
        ? `${API_BASE}/api/client-notifications/active`
        : `${API_BASE}/api/notifications`;
      
      const res = await axios.get(endpoint, {
        withCredentials: true
      });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      // Silently fail - notifications are non-critical
      console.debug('Notifications fetch failed:', err.message);
    }
  }, [authLoading, user]);

  useEffect(() => {
    // Defer notifications - non-critical
    const initialTimer = setTimeout(() => {
      fetchNotifications();
    }, 1500);
    
    // Detect mobile for optimized polling
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768;
    
    // Poll less frequently to reduce server load
    // Desktop: 60s, Mobile: 60s (was 10s/30s - too aggressive)
    const pollInterval = 60000;
    const interval = setInterval(fetchNotifications, pollInterval);
    
    // Also check when user returns to tab (more reliable than polling)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchNotifications();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Listen for user-authenticated event
    const handleUserAuthenticated = () => {
      setTimeout(fetchNotifications, 500);
    };
    window.addEventListener('user-authenticated', handleUserAuthenticated);
    
    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('user-authenticated', handleUserAuthenticated);
    };
  }, [fetchNotifications]); // Depends on fetchNotifications which depends on user

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkAsRead = async (notificationId, e) => {
    e?.stopPropagation();
    try {
      await axios.post(`${API_BASE}/api/notifications/${notificationId}/read`, {}, {
        withCredentials: true
      });
      fetchNotifications();
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleViewAll = () => {
    setIsOpen(false);
    // B2B users navigate to their portal's notifications page
    if (isB2BUser && b2bClientId) {
      navigate(`/portal/${b2bClientId}/notifications`);
    } else {
      navigate('/notifications');
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'alert': return <AlertCircle className="w-4 h-4" style={{ color: themeColors.secondary }} />;
      case 'promotion': return <Gift className="w-4 h-4" style={{ color: themeColors.primary }} />;
      case 'announcement': return <MessageSquare className="w-4 h-4" style={{ color: themeColors.secondary }} />;
      default: return <Info className="w-4 h-4" style={{ color: themeColors.primary }} />;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-3 lg:p-2.5 rounded-full border-2 transition-all"
        style={{
          borderColor: `${themeColors.primary}80`,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = themeColors.primary;
          e.currentTarget.style.backgroundColor = `${themeColors.primary}15`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = `${themeColors.primary}80`;
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
        data-testid="notification-bell"
      >
        <Bell className="w-6 h-6 lg:w-5 lg:h-5" style={{ color: `${themeColors.primary}B3` }} />
        {unreadCount > 0 && (
          <span 
            className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-xs font-bold text-white flex items-center justify-center animate-pulse"
            style={{ backgroundColor: themeColors.primary }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/80 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Centered Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div 
              className="w-full max-w-md rounded-2xl shadow-2xl shadow-black/50 overflow-hidden animate-in fade-in zoom-in-95 duration-200"
              style={{
                background: themeColors.background,
                border: `1px solid ${themeColors.border}`,
              }}
            >
              {/* Header */}
              <div 
                className="relative px-5 py-4"
                style={{ 
                  borderBottom: `1px solid ${themeColors.border}50`,
                  background: `${themeColors.backgroundSecondary}80`,
                }}
              >
                <h3 className="text-base font-semibold flex items-center justify-center gap-2" style={{ color: themeColors.text }}>
                  <Bell className="w-5 h-5" style={{ color: themeColors.primary }} />
                  Notifications
                </h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-white/10 transition-colors"
                  style={{ color: themeColors.textSecondary }}
                  data-testid="notif-dropdown-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Notifications List - with themed scrollbar */}
              <div 
                className="max-h-[400px] overflow-y-auto"
                style={{
                  scrollbarColor: `${themeColors.primary} ${themeColors.backgroundSecondary}`,
                }}
              >
                <style>{`
                  .notification-list::-webkit-scrollbar {
                    width: 8px;
                  }
                  .notification-list::-webkit-scrollbar-track {
                    background: ${themeColors.backgroundSecondary};
                  }
                  .notification-list::-webkit-scrollbar-thumb {
                    background: ${themeColors.primary};
                    border-radius: 4px;
                  }
                `}</style>
                {notifications.length === 0 ? (
                  <div className="px-5 py-6">
                    {/* All users see empty notification message - no onboarding tips here */}
                    <div className="text-center py-8">
                      <Bell className="w-12 h-12 mx-auto mb-3 opacity-30" style={{ color: themeColors.textSecondary }} />
                      <p className="text-sm font-medium" style={{ color: themeColors.text }}>No notifications yet</p>
                      <p className="text-xs mt-1" style={{ color: `${themeColors.textSecondary}80` }}>
                        Updates and announcements will appear here
                      </p>
                    </div>
                  </div>
                ) : (
                  notifications.slice(0, 5).map((notif, index) => (
                    <div
                      key={notif.notification_id}
                      className="px-5 py-4 transition-colors cursor-pointer"
                      style={{
                        borderBottom: `1px solid ${themeColors.border}30`,
                        background: !notif.is_read ? `${themeColors.primary}10` : 'transparent',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = `${themeColors.primary}20`}
                      onMouseLeave={(e) => e.currentTarget.style.background = !notif.is_read ? `${themeColors.primary}10` : 'transparent'}
                      onClick={() => {
                        handleMarkAsRead(notif.notification_id);
                        setIsOpen(false);
                        // B2B users navigate to their portal's notifications page
                        if (isB2BUser && b2bClientId) {
                          navigate(`/portal/${b2bClientId}/notifications?id=${notif.notification_id}`);
                        } else {
                          navigate(`/notifications?id=${notif.notification_id}`);
                        }
                      }}
                    >
                      <div className="flex items-start gap-4">
                        {/* Image or Icon */}
                        <div className="flex-shrink-0">
                          {notif.image_url ? (
                            <div 
                              className="w-12 h-12 rounded-xl overflow-hidden"
                              style={{ 
                                background: `${themeColors.backgroundSecondary}`,
                                border: `1px solid ${themeColors.border}50`,
                              }}
                            >
                              <img src={notif.image_url} alt="" className="w-full h-full object-cover" />
                            </div>
                          ) : (
                            <div 
                              className="w-12 h-12 rounded-xl flex items-center justify-center"
                              style={{ background: `${themeColors.primary}20` }}
                            >
                              {getIcon(notif.type)}
                            </div>
                          )}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-semibold leading-tight" style={{ color: themeColors.text }}>{notif.title}</p>
                            {!notif.is_read && (
                              <span 
                                className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1"
                                style={{ backgroundColor: themeColors.primary }}
                              />
                            )}
                          </div>
                          <p className="text-xs mt-1 line-clamp-2 leading-relaxed" style={{ color: themeColors.textSecondary }}>{notif.message}</p>
                          <p className="text-xs mt-2 font-medium" style={{ color: `${themeColors.textSecondary}80` }}>
                            {format(new Date(notif.created_at), 'MMM d, h:mm a')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Footer */}
              <div 
                className="px-5 py-4"
                style={{ 
                  borderTop: `1px solid ${themeColors.border}50`,
                  background: `${themeColors.backgroundSecondary}80`,
                }}
              >
                <button
                  onClick={handleViewAll}
                  className="w-full py-2.5 rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity"
                  style={{ 
                    background: themeColors.buttonBg,
                    color: themeColors.text,
                  }}
                >
                  View All Notifications
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Popup Modal for new notifications with image support and action buttons
// Accepts optional clientTheme for B2B portals to override the global theme
export const NotificationPopup = ({ onClose, clientTheme = null }) => {
  const [popup, setPopup] = useState(null);
  const [dismissing, setDismissing] = useState(false);
  const { theme: globalTheme } = useTheme();
  const { user, loading: authLoading } = useAuth();
  
  // Use clientTheme if provided (B2B), otherwise use global theme
  const theme = clientTheme || globalTheme;

  // Get theme colors with fallbacks - map B2B theme fields to expected format
  const themeColors = {
    background: theme?.background_secondary || theme?.card_color || theme?.background || '#1E293B',
    backgroundSecondary: theme?.background_primary || theme?.background || '#0F172A',
    primary: theme?.primary_color || theme?.primary || '#06B6D4',
    secondary: theme?.secondary_color || theme?.secondary || '#14B8A6',
    text: theme?.text_primary || theme?.text || '#F8FAFC',
    textSecondary: theme?.text_secondary || '#94A3B8',
    border: theme?.border_color || theme?.border || theme?.primary_color || theme?.primary || '#334155',
    buttonBg: theme?.mode === 'flat' 
      ? (theme?.primary_color || theme?.primary)
      : `linear-gradient(135deg, ${theme?.primary_color || theme?.primary || '#06B6D4'}, ${theme?.secondary_color || theme?.secondary || '#14B8A6'})`,
  };

  const checkPopup = async () => {
    // Wait for auth to be ready
    if (authLoading || !user) return;
    
    // Check if we've already dismissed popups this session
    const dismissedThisSession = sessionStorage.getItem('dismissed_popup_ids');
    const dismissedIds = dismissedThisSession ? JSON.parse(dismissedThisSession) : [];
    
    try {
      // Use user from AuthContext instead of making a separate /auth/me call
      // B2B users get their client's notifications, regular users get admin notifications
      const endpoint = user?.client_id 
        ? `${API_BASE}/api/client-notifications/active`
        : `${API_BASE}/api/notifications`;
      
      const res = await axios.get(endpoint, {
        withCredentials: true
      });
      if (res.data.popup_notification) {
        // Only show if not already dismissed in this session
        if (!dismissedIds.includes(res.data.popup_notification.notification_id)) {
          setPopup(res.data.popup_notification);
          // Track view
          trackInteraction(res.data.popup_notification.notification_id, 'view');
        }
      }
    } catch (err) {
      // Silently fail - popups are non-critical
      console.debug('Popup check failed:', err.message);
    }
  };

  useEffect(() => {
    // Defer popup check - non-critical
    const initialTimer = setTimeout(() => {
      checkPopup();
    }, 2000);
    
    // Poll for popup notifications less frequently to reduce server load
    // Was 5s/10s - changed to 60s to prevent CPU throttling
    const pollInterval = 60000;
    const interval = setInterval(checkPopup, pollInterval);
    
    // Also check when user returns to tab (visibility change) - more reliable than polling
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkPopup();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Check when window gains focus (reduces unnecessary checks)
    const handleFocus = () => {
      checkPopup();
    };
    window.addEventListener('focus', handleFocus);
    
    // Listen for user-authenticated event (instant popup on login)
    const handleUserAuthenticated = () => {
      console.log('[NotificationPopup] User authenticated - checking for popups');
      // Small delay to ensure backend has processed any pending notifications
      setTimeout(checkPopup, 500);
    };
    window.addEventListener('user-authenticated', handleUserAuthenticated);
    
    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('user-authenticated', handleUserAuthenticated);
    };
  }, [user, authLoading]); // Re-run when user changes

  const trackInteraction = async (notificationId, eventType, buttonIndex = null, buttonUrl = null) => {
    try {
      await axios.post(`${API_BASE}/api/notifications/${notificationId}/track`, {
        event_type: eventType,
        button_index: buttonIndex,
        button_url: buttonUrl
      }, {
        withCredentials: true
      });
    } catch (err) {
      console.error('Error tracking interaction:', err);
    }
  };

  const handleDismiss = async () => {
    if (!popup || dismissing) return;
    setDismissing(true);
    
    try {
      // Track dismissal
      await trackInteraction(popup.notification_id, 'dismiss');
      
      // Mark as dismissed in backend
      await axios.post(`${API_BASE}/api/notifications/${popup.notification_id}/dismiss-popup`, {}, {
        withCredentials: true
      });
      
      // Also track in sessionStorage to prevent re-showing this session
      const dismissedThisSession = sessionStorage.getItem('dismissed_popup_ids');
      const dismissedIds = dismissedThisSession ? JSON.parse(dismissedThisSession) : [];
      dismissedIds.push(popup.notification_id);
      sessionStorage.setItem('dismissed_popup_ids', JSON.stringify(dismissedIds));
      
      setPopup(null);
      if (onClose) onClose();
    } catch (err) {
      console.error('Error dismissing popup:', err);
      // Still close the popup locally even if the API call fails
      setPopup(null);
      if (onClose) onClose();
    } finally {
      setDismissing(false);
    }
  };

  const handleButtonClick = async (button, index) => {
    // Track button click
    await trackInteraction(popup.notification_id, 'click', index, button.url);
    
    // Normalize URL - add https:// if no protocol specified
    const normalizeUrl = (url) => {
      if (!url) return null;
      if (url.startsWith('http://') || url.startsWith('https://')) return url;
      if (url.startsWith('/')) return url; // Internal path
      return `https://${url}`;
    };
    
    // Navigate to the URL
    if (button.url) {
      const normalizedUrl = normalizeUrl(button.url);
      if (normalizedUrl.startsWith('http')) {
        window.open(normalizedUrl, '_blank');
      } else {
        window.location.href = normalizedUrl;
      }
    }
    
    // Dismiss popup after clicking action button
    handleDismiss();
  };

  if (!popup) return null;

  const getIcon = (type) => {
    switch (type) {
      case 'alert': return <AlertCircle className="w-6 h-6" style={{ color: themeColors.secondary }} />;
      case 'promotion': return <Gift className="w-6 h-6" style={{ color: themeColors.primary }} />;
      case 'announcement': return <MessageSquare className="w-6 h-6" style={{ color: themeColors.secondary }} />;
      default: return <Info className="w-6 h-6" style={{ color: themeColors.primary }} />;
    }
  };

  const actionButtons = popup.action_buttons || [];
  
  // Helper to get full image URL
  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/')) return `${API_BASE}${url}`;
    return url;
  };

  // Notification with image - SQUARE popup design
  if (popup.image_url) {
    return (
      <div 
        className="fixed inset-0 bg-black/90 flex items-center justify-center z-[100] p-4 md:p-6" 
        data-testid="notification-popup"
      >
        <div 
          className="rounded-2xl w-full max-w-md md:max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-300 shadow-2xl"
          style={{
            background: themeColors.background,
            border: `1px solid ${themeColors.border}`,
            boxShadow: `0 25px 50px -12px ${themeColors.primary}40`
          }}
        >
          {/* Header with close button */}
          <div 
            className="flex items-center justify-center px-5 py-4 relative"
            style={{ borderBottom: `1px solid ${themeColors.border}50` }}
          >
            <span 
              className="text-sm font-semibold uppercase tracking-wide"
              style={{ color: themeColors.primary }}
            >
              New Notification
            </span>
            <button
              onClick={handleDismiss}
              disabled={dismissing}
              className="absolute right-4 p-1.5 rounded-full hover:bg-white/10 transition-colors disabled:opacity-50"
              style={{ color: themeColors.textSecondary }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {/* Image - SQUARE aspect ratio */}
          <div 
            className="w-full aspect-square"
            style={{ background: themeColors.backgroundSecondary }}
          >
            <img 
              src={getImageUrl(popup.image_url)} 
              alt={popup.title}
              className="w-full h-full object-contain"
            />
          </div>
          
          {/* Content */}
          <div className="p-5">
            <h2 className="text-xl font-bold mb-2" style={{ color: themeColors.text }}>
              {popup.title}
            </h2>
            {popup.message && (
              <p 
                className="text-sm mb-5 leading-relaxed"
                style={{ color: themeColors.textSecondary }}
              >
                {popup.message}
              </p>
            )}
            
            {/* Action Buttons */}
            {actionButtons.length > 0 ? (
              <div className="flex flex-col gap-2">
                {actionButtons.map((button, idx) => (
                  <button
                    key={button.text || `action-btn-${idx}`}
                    onClick={() => handleButtonClick(button, idx)}
                    className="w-full py-3 rounded-xl font-semibold text-sm transition-opacity hover:opacity-90"
                    style={{
                      background: button.style === 'secondary' 
                        ? themeColors.backgroundSecondary
                        : themeColors.buttonBg,
                      color: themeColors.text,
                      border: button.style === 'secondary' 
                        ? `1px solid ${themeColors.border}` 
                        : 'none'
                    }}
                  >
                    {button.text}
                  </button>
                ))}
              </div>
            ) : (
              <button
                onClick={handleDismiss}
                disabled={dismissing}
                className="w-full py-3 rounded-xl font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-50"
                style={{ 
                  background: themeColors.buttonBg,
                  color: themeColors.text
                }}
              >
                {dismissing ? 'Closing...' : 'Got it!'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Text-only notification popup
  return (
    <div 
      className="fixed inset-0 bg-black/90 flex items-center justify-center z-[100] p-4 md:p-6" 
      data-testid="notification-popup"
    >
      <div 
        className="rounded-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-300 shadow-2xl"
        style={{
          background: themeColors.background,
          border: `1px solid ${themeColors.border}`,
          boxShadow: `0 25px 50px -12px ${themeColors.primary}40`
        }}
      >
        {/* Header with close button */}
        <div 
          className="flex items-center justify-between px-5 py-4"
          style={{ borderBottom: `1px solid ${themeColors.border}50` }}
        >
          <span 
            className="text-xs font-medium uppercase tracking-wide"
            style={{ color: themeColors.primary }}
          >
            New Notification
          </span>
          <button
            onClick={handleDismiss}
            disabled={dismissing}
            className="p-1.5 rounded-full hover:bg-white/10 transition-colors disabled:opacity-50"
            style={{ color: themeColors.textSecondary }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 text-center">
          <div 
            className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ background: `${themeColors.primary}25` }}
          >
            {getIcon(popup.type)}
          </div>
          
          <h2 className="text-lg font-bold mb-2" style={{ color: themeColors.text }}>
            {popup.title}
          </h2>
          <p 
            className="text-sm mb-6 leading-relaxed"
            style={{ color: themeColors.textSecondary }}
          >
            {popup.message}
          </p>

          {/* Action Buttons */}
          {actionButtons.length > 0 ? (
            <div className="flex flex-col gap-2">
              {actionButtons.map((button, idx) => (
                <button
                  key={button.text || `action-btn-${idx}`}
                  onClick={() => handleButtonClick(button, idx)}
                  className="w-full py-3 rounded-xl font-semibold text-sm transition-opacity hover:opacity-90"
                  style={{
                    background: button.style === 'secondary' 
                      ? themeColors.backgroundSecondary
                      : themeColors.buttonBg,
                    color: themeColors.text,
                    border: button.style === 'secondary' 
                      ? `1px solid ${themeColors.border}` 
                      : 'none'
                  }}
                >
                  {button.text}
                </button>
              ))}
            </div>
          ) : (
            <button
              onClick={handleDismiss}
              disabled={dismissing}
              className="w-full py-3 rounded-xl font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-50"
              style={{ 
                background: themeColors.buttonBg,
                color: themeColors.text
              }}
            >
              {dismissing ? 'Closing...' : 'Got it!'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationBell;
